/*
@chartFilterUpdating: used to record updating filters on each widget process
{
  widgetId: Boolean
}
*/ 


import { createContext, useContext } from "react";
import { useImmerReducer } from "use-immer";
import generateLocalFilters from "./generateLocalFiltersHelper";
// function to get a random id for widget or row
function getRandomId() {
    return Math.floor(Math.random() * 1000000);
}

const dashboardEditInit = {
  editedDashboard: {
    layout: { rows: [], drafts: {} }, //v4 layout
  },
  deleteWidget: {},
  isUpdatingDashboard: false,
  isSharedDashboardModalOpen: false,
  isPublishDashboardModalOpen: false,
  previewImg: null,
  isEditable: true,
  chartIdsFromDraftsToRows: [],
  globalFilters: {},
  localFilters: {},
  filterChanged: false,
  widgetGrouping: [], //used to seperate the building founding of local filters from the actual widgets since the widgets related to the layout, which triggered the applyFilters, we need to seperate the filters from widgets in future
  widgets: [],
  selectedWidgets: [],
  selectedGroups: [],
  bannerEvent: null,
  numberOfGroups: 0,
  dashboardFilters: {},
  dashboardViews: [],
  openFilterNav: false,
  chartFilterUpdating: {},
};

function removeFromRow(draft, rowIndex, widget) {
  draft.editedDashboard.layout.rows[rowIndex].widgets = draft.editedDashboard.layout.rows[rowIndex].widgets.filter(
    (w) => w.id !== widget.id
  );

    //get the length of the widgets in that row
    const widgetsInRow = draft.editedDashboard.layout.rows[rowIndex].widgets.length;

    //if there are no more widgets in that row, remove the row altogether
    if (widgetsInRow === 0) {
      draft.editedDashboard.layout.rows.splice(rowIndex, 1);
    } else {
      //resize each remaining widget to an equal factor of 12
      draft.editedDashboard.layout.rows[rowIndex].widgets.forEach((w) => {
        w.size = 12/widgetsInRow;
      });
    }
  }

function addWidgetToRow(draft, rowIndex, widget, widgetIndex) {
  draft.editedDashboard.layout.rows[rowIndex].widgets.splice(widgetIndex, 0, {...widget});

  //get the length of the widgets in that row
  const widgetsInRow = draft.editedDashboard.layout.rows[rowIndex].widgets.length;

  //resize each remaining widget to an equal factor of 12
  draft.editedDashboard.layout.rows[rowIndex].widgets.forEach((w) => {
    w.size = 12/widgetsInRow;
  });
}

function removeFromDrafts(draft, widget) {
  //if the widget is in drafts, remove it
  if (draft.editedDashboard.layout.drafts[widget.id]) {
    delete draft.editedDashboard.layout.drafts[widget.id];
  }
}


//accepts a chart
export function configureWidgetFromChart({ id, title, type }) {
  const colors = ['red', 'orange','yellow','green','blue','indigo','violet', 'coral', 'chocolate', 'crimson', 'aqua', 'cornsilk'];
  return {
    id,
    title,
    ChartId: id,
    type,
    backgroundColor: colors[Math.floor(Math.random()*colors.length)] || 'green',
  }
}

function dashboardEditReducer(draft, { type, payload }) {
    switch (type) {
      case "setEditedDashboard": {
        draft.editedDashboard = payload;
        break;
      }
      case "setChartFilterUpdating": {
        payload.forEach((widgetId) => {
          draft.chartFilterUpdating[widgetId] = true;
        });
        break;
      }
      case "updateChartFilterUpdating": {
        draft.chartFilterUpdating[payload.widgetId] = payload.value;
        break;
      }
      case "setIsUpdatingDashboard": {
        draft.isUpdatingDashboard = payload;
        break;
      }
      case "setIsSharedDashboardModalOpen": {
        draft.isSharedDashboardModalOpen = payload;
        break;
      }
      case "setDashboardPublishModal": {
        draft.isPublishDashboardModalOpen = !draft.isPublishDashboardModalOpen;
        break;
      }
      case "changeEditedDashboardName": {
        draft.editedDashboard.title = payload;
        break;
      }
      case "updateAccountDashboardPermissionId": {
        draft.editedDashboard.AccountDashboardPermissionId = payload;
        break;
      }
      case "passPreviewImg": {
        draft.previewImg = payload;
        break;
      }
      case "changeDescription": {
        let editedDash = JSON.parse(JSON.stringify(draft.editedDashboard));
        draft.editedDashboard = { ...editedDash, description: payload };
        break;
      }
      case "changeTitle": {
        let editedDash = JSON.parse(JSON.stringify(draft.editedDashboard));
        draft.editedDashboard = { ...editedDash, title: payload };
        break;
      }
      case "changeLayout": {
        draft.editedDashboard.layout = payload;
        break;
      }
      case "addRow": {
        let newRowIndex = payload.rowIndex;

        //if an object is passed to removeFromRow, remove the widget from that row first
        if (payload.removeFromRow) {
          //if the widget to be removed is in a row by itself, that row will be removed and we may need to adjust the newRowIndex
          if (
            draft.editedDashboard.layout.rows[payload.removeFromRow.rowIndex]
              ?.widgets.length === 1
          ) {
            //if the newRowIndex is the same or right after the rowIndex of the widget to be removed, no action is needed, so break
            if (
              newRowIndex === payload.removeFromRow.rowIndex ||
              newRowIndex === payload.removeFromRow.rowIndex + 1
            ) {
              break;
            } else {
              //otherwise, if the newRowIndex is more than 1 plus the current one, we need to subtract 1 from it bc the old row will be removed before the new one is added
              if (newRowIndex > payload.removeFromRow.rowIndex + 1) {
                newRowIndex = newRowIndex - 1;
              }
            }
          }
          removeFromRow(
            draft,
            payload.removeFromRow.rowIndex,
            payload.removeFromRow.widget
          );
        }

        //otherwise, check to see if it's in drafts and if so, remove it
        else {
          removeFromDrafts(draft, payload.widget);
        }

        //add the new row
        draft.editedDashboard.layout.rows.splice(newRowIndex, 0, {
          id: payload.id || getRandomId(),
          height: payload.row?.height || 277,
          widgets: payload.row?.widgets || [],
        });

        break;
      }
      case "addToRow": {
        let newRowIndex = payload.rowIndex;
        let newWidgetIndex = payload.widgetIndex;

        //if an object is passed to removeFromRow, remove the widget from that row first
        if (payload.removeFromRow) {
          //if the widget to be removed is in a row by itself, that row will be removed and we may need to adjust the newRowIndex
          if (
            draft.editedDashboard.layout.rows[payload.removeFromRow.rowIndex]
              ?.widgets.length === 1
          ) {
            //if the newRowIndex is more than the widget's current one, we need to subtract 1 from it bc the old row will be removed before the widget is added
            if (newRowIndex > payload.removeFromRow.rowIndex) {
              newRowIndex = newRowIndex - 1;
            }
          }

          //we also may need to adjust the new widget index if its row index is the same as the one we are changing
          if (payload.rowIndex === payload.removeFromRow.rowIndex) {
            //if the widget's index is the same, or one more than it's current index, it is not really moving, so do nothing and break
            if (
              newWidgetIndex === payload.removeFromRow.widgetIndex ||
              newWidgetIndex === payload.removeFromRow.widgetIndex + 1
            ) {
              break;
            } else {
              //otherwise, if the new widget index is greater than its current one, the new index has to be reduced by one because the row will be one widget shorter
              if (newWidgetIndex > payload.removeFromRow.widgetIndex) {
                newWidgetIndex = newWidgetIndex - 1;
              }
            }
          }
          removeFromRow(
            draft,
            payload.removeFromRow.rowIndex,
            payload.removeFromRow.widget
          );
        }

        //otherwise, check to see if it's in drafts and if so, remove it
        else {
          removeFromDrafts(draft, payload.widget);
        }

        addWidgetToRow(draft, newRowIndex, payload.widget, newWidgetIndex);
        break;
      }
      // changeRow is called when a row's layout changes (i.e. resize)
      case "changeRow": {
        //replace the row at the given rowIndex
        draft.editedDashboard.layout.rows[payload.rowIndex] = payload.row;
        break;
      }
      case "setIsEditable": {
        draft.isEditable = payload;
        break;
      }
      //accepts a chart object as the payload
      case "addDraftChartWidget": {
        draft.editedDashboard.layout.drafts[payload.id] = payload;
        break;
      }
      //transfer a chart from the layout to drafts, payload: {rowIndex, widget}
      case "transferToDrafts": {
        removeFromRow(draft, payload.rowIndex, payload.widget);
        draft.editedDashboard.layout.drafts[payload.widget.id] = payload.widget;
        break;
      }
      //delete a chart from the row, payload: {rowIndex, widget}
      case "deleteChartFromRow": {
        removeFromRow(draft, payload.rowIndex, payload.widget);
        //update the widgetGrouping
        draft.widgetGrouping = draft.widgetGrouping.filter(
          (w) => w.widgetId !== payload.widget.id
        );
        break;
      }
      //delete a chart from the drafts, payload: {rowIndex, widget}
      case "deleteChartFromDrafts": {
        removeFromDrafts(draft, payload.widget);
        break;
      }
      //transfer a chart from the drafts to layout as the first row, payload: {ChartId || id}
      case "transferToLayout": {
        let draftWidget;
        if (payload.id) {
          draftWidget = draft.editedDashboard.layout.drafts[payload.id];
        } else if (payload.ChartId) {
          draftWidget = Object.values(draft.editedDashboard.layout.drafts).find(
            (dw) => dw.ChartId === payload.ChartId
          );
        }
        if (draftWidget) {
          removeFromDrafts(draft, draftWidget);
          //add the new row
          draft.editedDashboard.layout.rows.splice(0, 0, {
            id: getRandomId(),
            height: payload.row?.height || 277,
            widgets: [draftWidget],
          });
        }
        break;
      }
      case "duplicateWidgetToLayout": {
        draft.editedDashboard.layout.rows.splice(0, 0, {
          id: getRandomId(),
          height: 277,
          widgets: [
            {
              title: `Copy of widget`,
              skeleton: true,
              id: `skeleton`,
              type: null,
            },
          ],
        });
        break;
      }
      case "replaceSkeletonWithWidget": {
        draft.editedDashboard.layout.rows[0] = {
          id: getRandomId(),
          height: 277,
          widgets: [payload],
        };
        break;
      }
      //generate the grid table from the chart and add it in the row below: {rowIndex, widget}
      case "generateGridToLayout": {
        // Use find to get the widget that matches the condition
        let foundWidget;
        Object.values(draft.editedDashboard.layout.rows).forEach((row) => {
          const widget = row.widgets.find(
            (widget) => widget.ChartId === payload.widget.ChartId
          );
          if (widget) {
            foundWidget = widget;
          }
        });

        if (foundWidget) {
          //add the new row
          draft.editedDashboard.layout.rows.splice(payload.rowIndex + 1, 0, {
            id: getRandomId(),
            height: 370,
            widgets: [
              {
                ...foundWidget,
                size: 12,
                generatedGrid: payload.generatedGrid,
              },
            ],
          });
        }
        break;
      }
      //delete a chart from this dashbaord
      case "chartToDeleteFromDash": {
        draft.deleteWidget = payload;
        break;
      }
      //remember the IDs of charts adding from drafts to rows
      case "addChartFromDraftsToRows": {
        if (!draft.chartIdsFromDraftsToRows.includes(payload)) {
          //exclude the situation that first add, then move back to drafts and then adding to rows again
          draft.chartIdsFromDraftsToRows.push(payload);
        }
        break;
      }
      case "setGlobalFilters": {
        draft.globalFilters = payload;
        break;
      }
      // addToFilterSet adds a value to a filter set; it adds the whole filter set if it doesn't already exist
      case "addToFilterSet": {
        const { column, value } = payload;
        //create the filter set if it doesn't exist
        if (!draft.globalFilters[column]) {
          draft.globalFilters[column] = {
            filterType: "set",
            values: [value],
          };
        } else {
          //log an error if the filterType is not "set"
          if (draft.globalFilters[column].filterType !== "set") {
            console.error(
              `Could not add value ${value} to filter set ${column}, filter type is not "set"`
            );
          }
          draft.globalFilters[column].values.push(value);
        }
        break;
      }
      case "removeFromFilterSet": {
        const { column, value } = payload;
        //log an error if the filter does not exist
        if (!draft.globalFilters[column]) {
          console.error(
            `Could not remove value ${value} from filter set ${column}, filter does not exist`
          );
        }

        //remove the value from the filter set
        draft.globalFilters[column].values = draft.globalFilters[
          column
        ].values.filter((v) => v !== value);

        //if the set is now empty, remove the column altogether
        if (draft.globalFilters[column].values.length === 0) {
          delete draft.globalFilters[column];
        }
        break;
      }
      case "setFilterSettingEnabled": {
        const { column, enabled } = payload;
        if (!draft.editedDashboard.filterSettings) {
          draft.editedDashboard.filterSettings = {};
        }
        if (!draft.editedDashboard?.filterSettings?.[column]) {
          draft.editedDashboard.filterSettings[column] = {};
        }
        draft.editedDashboard.filterSettings[column].enabled = enabled;
        break;
      }
      case "setFilterSettings": {
        draft.editedDashboard.filterSettings = payload;
        break;
      }
      case "applyCrossFilter": {
        const { column, value } = payload;
        //check to see the column and value filter is already in place
        const alreadyFiltered =
          draft.globalFilters[column]?.values.includes(value);
        if (alreadyFiltered) {
          //reset the filters
          draft.globalFilters = {};
        } else {
          //set the filters to only this column and value
          draft.globalFilters = {
            [column]: {
              filterType: "set",
              values: [value],
            },
          };
        }
        break;
      }
      case "setLocalFilters": {
        draft.localFilters = payload;
        break;
      }
      case "updateLocalFilters": {
        const localFilterList = generateLocalFilters(payload);
        draft.localFilters = localFilterList;
        break;
      }
      case "setFilterChanged": {
        draft.filterChanged = payload;
        break;
      }
      case "addToLocalFilter": {
        const { value, controlCardId, widgetId } = payload;

        // Find the widget in the widgetGrouping
        const widget = draft.widgetGrouping.find(
          (w) => w.widgetId === widgetId
        );
        if (!widget) return;

        // Find the filterSetting in the wigetGrouping
        const filterSetting = widget.filterSettings.find(
          (fs) => fs.controlCardId === controlCardId
        );
        if (!filterSetting) return;

        // Update the filterSetting value in the wigetGrouping
        filterSetting.value = [...filterSetting.value, ...value];

        // Use the widgetGrouping to generate the updated localFilters
        const localFilterList = generateLocalFilters(draft.widgetGrouping);
        draft.localFilters = localFilterList;
        break;
      }

      case "removeFromLocalFilter": {
        const { column, value, groupId, controlCardId, widgetId } = payload;

        // Find the widget in widgetGrouping
        const widget = draft.widgetGrouping.find(
          (w) => w.widgetId === widgetId
        );
        if (!widget) return;

        // Find the filterSetting in the wigetGrouping
        const filterSetting = widget.filterSettings.find(
          (fs) => fs.controlCardId === controlCardId
        );
        if (!filterSetting) return;

        // Update the filterSetting value in the wigetGrouping by removing the specified values
        filterSetting.value = filterSetting.value.filter(
          (v) => !value.includes(v)
        );

        // Log an error if the filter does not exist in the localFilters
        if (!draft.localFilters?.[groupId]?.[column]) {
          console.error(
            `Could not remove value ${value} from filter set ${column}, filter does not exist`
          );
          return;
        }
        // Use the widgetGrouping to generate the updated localFilters
        const localFilterList = generateLocalFilters(draft.widgetGrouping);

        // Remove the column altogether if it has no values left in the filter
        for (const [column, fs] of Object.entries(
          localFilterList[groupId] || {}
        )) {
          if (fs.values.length === 0) {
            delete localFilterList[groupId][column];
          }
        }

        // Set the modified localFilters to the draft
        draft.localFilters = localFilterList;
        break;
      }

      case "setWidgets": {
        draft.widgets = payload;
        break;
      }

      case "setWidgetGrouping": {
        draft.widgetGrouping = payload;
        // Use the widgetGrouping to generate the updated localFilters
        if (payload?.length > 0) {
          const localFilterList = generateLocalFilters(payload);
          draft.localFilters = localFilterList;
        }
        break;
      }

      case "selectWidget": {
        const { widgetId } = payload;
        if (!draft.selectedWidgets.includes(widgetId)) {
          draft.selectedWidgets.push(widgetId); // Add the widget to selection
        } else {
          draft.selectedWidgets = draft.selectedWidgets.filter(
            (id) => id !== widgetId
          ); // Deselect if already selected
        }
        break;
      }
      case "clearSelectedWidgets": {
        draft.selectedWidgets = [];
        break;
      }
      case "selectGroup": {
        const { groupId } = payload;
        if (!draft.selectedGroups.includes(groupId)) {
          draft.selectedGroups.push(groupId); // Add the group to selection
        } else {
          draft.selectedGroups = draft.selectedGroups.filter(
            (id) => id !== groupId
          ); // Deselect if already selected
        }

        // Automatically update selected widgets for the group
        draft.widgets.forEach((widget) => {
          if (widget.group === groupId) {
            if (!draft.selectedWidgets.includes(widget.widgetId)) {
              draft.selectedWidgets.push(widget.widgetId);
            } else {
              draft.selectedWidgets = draft.selectedWidgets.filter(
                (id) => id !== widget.widgetId
              ); // Deselect if already selected
            }
          }
        });

        break;
      }
      case "eraseBanner": {
        draft.bannerEvent = null;
        break;
      }
      case "addWidgetToGroup": {
        const { widgetId, groupId } = payload;
        const widget = draft.widgets.find((i) => i.widgetId === widgetId);
        if (widget) {
          widget.group = groupId;
        }
        const widgetInGroup = draft.widgetGrouping.find(
          (i) => i.widgetId === widgetId
        );
        if (widgetInGroup) {
          widgetInGroup.group = groupId;

          if (widgetInGroup.controlCard && widgetInGroup.filterSettings) {
            // Use the widgetGrouping to generate the updated localFilters
            const localFilterList = generateLocalFilters(draft.widgetGrouping);
            draft.localFilters = localFilterList;
          }
        }
        break;
      }
      case "clearSelectedGroups": {
        draft.selectedGroups = [];
        draft.selectedWidgets = [];
        break;
      }
      case "groupWidgets": {
        const existingGroups = new Set(
          draft.widgets.map((widget) => widget.group).filter(Boolean)
        );
        if (existingGroups.size >= 4 && !payload.groupId) {
          draft.bannerEvent = {
            mainText: "Only 5 groups can be formed at a time.",
            subText: "",
            tallBanner: false,
            success: false,
          };
          return;
        }
        const groupId = `Untitled Group ${draft.numberOfGroups}`;
        draft.numberOfGroups++;
        payload.selectedIds.forEach((id) => {
          const widget = draft.widgets.find((i) => i.widgetId === id);
          if (widget) widget.group = groupId;
          //We need to update the widgetgrouping here too since it is used in the local filters, we need to seperate it out from widgets in figure
          const widgeInGroup = draft.widgetGrouping.find(
            (i) => i.widgetId === id
          );
          if (widgeInGroup) widgeInGroup.group = groupId;
        });

        // Use the widgetGrouping to generate the updated localFilters
        const localFilterList = generateLocalFilters(draft.widgetGrouping);
        draft.localFilters = localFilterList;

        break;
      }
      case "changeGroupName": {
        const { newGroupName, oldGroupName } = payload;
        draft.widgets.forEach((widget) => {
          if (widget.group === oldGroupName) {
            widget.group = newGroupName;
          }
        });
        break;
      }
      case "ungroupWidgets": {
        const groupId = payload.groupId;

        // Ungroup widgets and clear their selection
        draft.widgets.forEach((widget) => {
          if (widget.group === groupId) {
            widget.group = null;

            // Remove the widget from selectedWidgets
            const selectedIndex = draft.selectedWidgets.indexOf(
              widget.widgetId
            );
            if (selectedIndex > -1) {
              draft.selectedWidgets.splice(selectedIndex, 1);
            }
          }
        });
        //same here, update widgetGrouping, why we update in widgetGrouping not copy the widgets is that if user changed the filters before grouping aciton, the changes in filters will be kept in widgetGrouping, not in widgets, so if we simply copy the widgets, the filters will be reset
        draft.widgetGrouping.forEach((widget) => {
          if (widget.group === groupId) {
            widget.group = null;
          }
        });
        // Use the widgetGrouping to generate the updated localFilters
        const localFilterList = generateLocalFilters(draft.widgetGrouping);
        draft.localFilters = localFilterList;
        break;
      }
      case "removeFromGroup": {
        const removeWidget = (widgetId, drawer) => {
          const widget = draft[drawer].find((i) => i.widgetId === widgetId);
          if (widget) widget.group = null;

          // Check for groups with fewer than 2 members and ungroup them
          const groups = draft[drawer].reduce((acc, widget) => {
            if (widget.group) {
              acc[widget.group] = (acc[widget.group] || 0) + 1;
            }
            return acc;
          }, {});

          Object.keys(groups).forEach((groupId) => {
            if (groups[groupId] < 2) {
              draft[drawer].forEach((widget) => {
                if (widget.group === groupId) {
                  widget.group = null;
                }
              });
            }
          });
        };
        removeWidget(payload.widgetId, "widgets");
        removeWidget(payload.widgetId, "widgetGrouping");
        // Use the widgetGrouping to generate the updated localFilters
        const localFilterList = generateLocalFilters(draft.widgetGrouping);
        draft.localFilters = localFilterList;
        draft.selectedWidgets = draft.selectedWidgets.filter(
          (id) => id !== payload.widgetId
        );

        break;
      }
      case "moveWidgetToAnotherGroup": {
        const moveWidgetToAnotherDrawer = (widgetId, newGroupId, drawer) => {
          const widget = draft[drawer].find((i) => i.widgetId === widgetId);
          if (widget) widget.group = newGroupId;

          // Check for groups with only one widget and ungroup them
          const groups = draft[drawer].reduce((acc, widget) => {
            if (widget.group) {
              acc[widget.group] = (acc[widget.group] || 0) + 1;
            }
            return acc;
          }, {});

          Object.keys(groups).forEach((groupId) => {
            if (groups[groupId] < 2) {
              draft[drawer].forEach((widget) => {
                if (widget.group === groupId) {
                  widget.group = null;
                }
              });
            }
          });
        };

        const { widgetId, newGroupId } = payload;

        moveWidgetToAnotherDrawer(widgetId, newGroupId, "widgets");
        moveWidgetToAnotherDrawer(widgetId, newGroupId, "widgetGrouping");
        // Use the widgetGrouping to generate the updated localFilters
        const localFilterList = generateLocalFilters(draft.widgetGrouping);
        draft.localFilters = localFilterList;

        break;
      }
      case "reorderWidgets": {
        const { dragIndex, hoverIndex, groupId } = payload;

        // Filter widgets by group
        const groupWidgets = draft.widgets.filter(
          (widget) => widget.group === groupId
        );

        // Reorder widgets within the group
        const draggedWidget = groupWidgets[dragIndex];
        groupWidgets.splice(dragIndex, 1);
        groupWidgets.splice(hoverIndex, 0, draggedWidget);

        // Update draft widgets array
        draft.widgets = draft.widgets.map((widget) =>
          groupWidgets.includes(widget)
            ? groupWidgets[groupWidgets.indexOf(widget)]
            : widget
        );
        break;
      }

      case "setOpenFilterNav": {
        draft.openFilterNav = payload;
        break;
      }
      default: {
        return null;
      }
    }
}

const [DashboardEditContext, DashboardEditDispatchContext] = [
    createContext(null),
    createContext(null),
];

export default function DashboardProvider({ children, init }) {
    const [dashboardEditStore, dispatch] = useImmerReducer(
      dashboardEditReducer,
      init || dashboardEditInit
    );
  
    return (
      <DashboardEditContext.Provider value={dashboardEditStore}>
        <DashboardEditDispatchContext.Provider value={dispatch}>
          {children}
        </DashboardEditDispatchContext.Provider>
      </DashboardEditContext.Provider>
    );
  }
  
  export function useDashboardStore() {
    return useContext(DashboardEditContext);
  }
  
  export function useDashboardDispatch() {
    return useContext(DashboardEditDispatchContext);
  }